import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Calendar,
  ChevronDown,
  Check,
  X,
} from "lucide-react";

export interface DateRange {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

export interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (range: DateRange) => void;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  className?: string;
}

export const formatYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const parseYMD = (s?: string): Date | null => {
  if (!s) return null;
  const parts = s.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

export const formatDisplayDate = (dateStr?: string): string => {
  if (!dateStr) return "Select date";
  const d = parseYMD(dateStr);
  if (!d || isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getDefaultDateRange = (): DateRange => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
  return {
    startDate: formatYMD(start),
    endDate: formatYMD(today),
  };
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Staged selection while popover is open
  const [tempStart, setTempStart] = useState<string>(startDate);
  const [tempEnd, setTempEnd] = useState<string>(endDate);

  // Today string
  const todayStr = useMemo(() => formatYMD(new Date()), []);
  const effectiveMaxDate = maxDate || todayStr;

  // Sync temp values whenever props change or dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTempStart(startDate);
      setTempEnd(endDate);
    }
  }, [isOpen, startDate, endDate]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Escape key listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Quick preset ranges
  const presets = useMemo(() => {
    const now = new Date();
    return [
      {
        label: "Last 30 Days",
        getRange: () => {
          const s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
          return { startDate: formatYMD(s), endDate: formatYMD(now) };
        },
      },
      {
        label: "Last 3 Months",
        getRange: () => {
          const s = new Date(now.getFullYear(), now.getMonth() - 2, 1);
          return { startDate: formatYMD(s), endDate: formatYMD(now) };
        },
      },
      {
        label: "Last 6 Months",
        getRange: () => {
          const s = new Date(now.getFullYear(), now.getMonth() - 5, 1);
          return { startDate: formatYMD(s), endDate: formatYMD(now) };
        },
      },
      {
        label: "Year to Date",
        getRange: () => {
          const s = new Date(now.getFullYear(), 0, 1);
          return { startDate: formatYMD(s), endDate: formatYMD(now) };
        },
      },
      {
        label: "Last 12 Months",
        getRange: () => {
          const s = new Date(now.getFullYear() - 1, now.getMonth() + 1, 1);
          return { startDate: formatYMD(s), endDate: formatYMD(now) };
        },
      },
    ];
  }, []);

  const handleApplyPreset = (getRange: () => DateRange) => {
    const { startDate: s, endDate: e } = getRange();
    setTempStart(s);
    setTempEnd(e);
    onChange({ startDate: s, endDate: e });
    setIsOpen(false);
  };

  const handleApply = () => {
    if (!tempStart) return;
    const finalStart = tempStart;
    const finalEnd = tempEnd || tempStart;
    onChange({ startDate: finalStart, endDate: finalEnd });
    setIsOpen(false);
  };

  return (
    <div className={`relative inline-block text-left ${className}`} ref={containerRef}>
      {/* ── Single Unified Trigger Button ── */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-expanded={isOpen}
        title="Click to select date range"
        className={`inline-flex items-center gap-2 rounded-xl border border-[#E4EAF0] bg-white px-3 py-1.5 text-xs font-semibold text-[#10253A] shadow-sm hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#15BBAE]/20 ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        } ${isOpen ? "border-[#15BBAE] ring-2 ring-[#15BBAE]/20" : ""}`}
      >
        <Calendar size={14} className="text-[#15BBAE] shrink-0" />
        <span className="truncate max-w-[200px] sm:max-w-none">
          {formatDisplayDate(startDate)} — {formatDisplayDate(endDate)}
        </span>
        <ChevronDown
          size={13}
          className={`text-[#63788E] transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180 text-[#15BBAE]" : ""
          }`}
        />
      </button>

      {/* ── Unified Date Range Popover ── */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[300px] sm:w-[320px] rounded-2xl border border-[#E4EAF0] bg-white p-4 shadow-xl text-xs max-w-[95vw]">
          {/* Popover Header with Close Button */}
          <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-[#F0F4F8]">
            <div className="flex items-center gap-1.5 font-bold text-[#10253A]">
              <Calendar size={14} className="text-[#15BBAE]" />
              <span>Select Date Range</span>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-lg text-[#63788E] hover:text-[#10253A] hover:bg-[#F1F5F9] transition-colors cursor-pointer"
            >
              <X size={14} />
            </button>
          </div>

          {/* Quick Presets */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {presets.map((preset) => {
              const range = preset.getRange();
              const isSelected = tempStart === range.startDate && tempEnd === range.endDate;
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handleApplyPreset(preset.getRange)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all cursor-pointer ${
                    isSelected
                      ? "bg-[#E6F7F5] text-[#15BBAE] font-bold border border-[#CDEFEA]"
                      : "bg-[#F8FAFC] text-[#63788E] hover:text-[#10253A] hover:bg-[#F1F5F9] border border-[#E4EAF0]/60"
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          {/* Coordinated From / To Inputs inside the Popover */}
          <div className="grid grid-cols-2 gap-2 mb-3 bg-[#F8FAFC] p-2.5 rounded-xl border border-[#E4EAF0]">
            <div>
              <label className="block text-[10px] font-semibold text-[#63788E] uppercase tracking-wider mb-1">
                From Date
              </label>
              <input
                type="date"
                value={tempStart}
                min={minDate}
                max={tempEnd || effectiveMaxDate}
                onChange={(e) => setTempStart(e.target.value)}
                className="w-full bg-white border border-[#E4EAF0] rounded-lg px-2 py-1.5 text-xs text-[#10253A] font-medium focus:outline-none focus:ring-1 focus:ring-[#15BBAE] cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-[#63788E] uppercase tracking-wider mb-1">
                To Date
              </label>
              <input
                type="date"
                value={tempEnd}
                min={tempStart || minDate}
                max={effectiveMaxDate}
                onChange={(e) => setTempEnd(e.target.value)}
                className="w-full bg-white border border-[#E4EAF0] rounded-lg px-2 py-1.5 text-xs text-[#10253A] font-medium focus:outline-none focus:ring-1 focus:ring-[#15BBAE] cursor-pointer"
              />
            </div>
          </div>

          {/* Footer with Selected Range Display, Cancel, and Apply */}
          <div className="flex items-center justify-between pt-2.5 border-t border-[#F0F4F8] gap-2">
            <div className="text-[11px] text-[#63788E] truncate">
              {tempStart ? (
                <span>
                  {formatDisplayDate(tempStart)}
                  {tempEnd && tempEnd !== tempStart ? ` – ${formatDisplayDate(tempEnd)}` : ""}
                </span>
              ) : (
                <span className="text-[#8A929B]">No date selected</span>
              )}
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-2.5 py-1.5 rounded-lg border border-[#E4EAF0] bg-white text-[#63788E] hover:text-[#10253A] hover:bg-[#F8FAFC] text-xs font-medium transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!tempStart}
                className="px-3.5 py-1.5 rounded-lg bg-[#15BBAE] hover:bg-[#13A89D] text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Check size={13} />
                <span>Apply</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
